import { useState, useEffect, useMemo, useCallback } from 'react';

// Helper to access nested object properties e.g. 'systemLookupCode.name'
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return '';
  if (!path.includes('.')) return obj[path];
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : ''), obj);
};

/**
 * useDragSortTable
 * Custom hook managing drag-and-drop manual sequencing, column sorting,
 * and reorder mode lifecycle for table components.
 */
export const useDragSortTable = ({
  rows = [],
  rowKey = 'id',
  sequenceField = 'sequence',
  onConfirmReorder,
  isAnyRowEditing = false,
  onReorderModeChange
}) => {
  const [localRows, setLocalRows] = useState(rows);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Notify parent of reorder mode change
  useEffect(() => {
    onReorderModeChange?.(isReorderMode);
  }, [isReorderMode, onReorderModeChange]);

  // Sorting state (asc/desc by column field)
  const [orderBy, setOrderBy] = useState(sequenceField);
  const [order, setOrder] = useState('asc');

  // Synchronize when parent rows change
  useEffect(() => {
    setLocalRows(rows);
    setIsReorderMode(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [rows]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      setLocalRows([]);
    };
  }, []);

  // Column Sort Handler (disabled while in reorder mode)
  const handleRequestSort = useCallback((field) => {
    if (isReorderMode) return;
    setOrder((prevOrder) => (orderBy === field && prevOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy(field);
  }, [isReorderMode, orderBy]);

  // Enter Reorder Mode: locks ordering to the natural sequence
  const handleEnterReorderMode = useCallback(() => {
    setIsReorderMode(true);
    setOrderBy(sequenceField);
    setOrder('asc');
  }, [sequenceField]);

  // Cancel Reorder Mode: reverts rows back to initial order
  const handleCancelReorder = useCallback(() => {
    setLocalRows(rows);
    setIsReorderMode(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [rows]);

  // Confirm and Save New Order
  const handleConfirmReorder = useCallback(async () => {
    if (!onConfirmReorder) return;
    setIsSavingOrder(true);
    const orderedIds = localRows.filter((r) => !r.isNew).map((r) => r[rowKey]);
    try {
      await onConfirmReorder(orderedIds, localRows);
      setIsReorderMode(false);
    } catch (err) {
      console.error('Failed to confirm reorder', err);
    } finally {
      setIsSavingOrder(false);
    }
  }, [localRows, onConfirmReorder, rowKey]);

  // HTML5 Drag & Drop Handlers (memoized)
  const handleDragStart = useCallback((e, index) => {
    if (!isReorderMode || isAnyRowEditing) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }, [isReorderMode, isAnyRowEditing]);

  const handleDragOver = useCallback((e, index) => {
    if (!isReorderMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex((prev) => (prev !== index ? index : prev));
  }, [isReorderMode]);

  const handleDragLeave = useCallback(() => {
    // Drop target leave
  }, []);

  const handleDrop = useCallback((e, targetIndex) => {
    if (!isReorderMode) return;
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setLocalRows((prevRows) => {
      const updated = [...prevRows];
      const [movedItem] = updated.splice(draggedIndex, 1);
      updated.splice(targetIndex, 0, movedItem);
      return updated;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [isReorderMode, draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Display rows calculation (memoized)
  const displayRows = useMemo(() => {
    if (isReorderMode || !orderBy) {
      return localRows;
    }

    return [...localRows].sort((a, b) => {
      let aVal = getNestedValue(a, orderBy);
      let bVal = getNestedValue(b, orderBy);

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal), undefined, { numeric: true })
        : String(bVal).localeCompare(String(aVal), undefined, { numeric: true });
    });
  }, [localRows, isReorderMode, orderBy, order]);

  return {
    localRows,
    setLocalRows,
    displayRows,
    isReorderMode,
    isSavingOrder,
    draggedIndex,
    dragOverIndex,
    orderBy,
    order,
    handleRequestSort,
    handleEnterReorderMode,
    handleCancelReorder,
    handleConfirmReorder,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd
  };
};

export default useDragSortTable;
